import { useRef, useEffect, useState } from 'react'
import gsap from 'gsap'
import TransitionLink from '../shell/TransitionLink.jsx'
import { transitionTo } from '../../utils/transitions'

// Photos ordered by photography-page priority — user-specified swaps
const CARDS_ROW1 = [
  { id: 'g1', left: 55,   top: 95,  rotate: -8.5, src: '/photos/grid/N005.webp' },           // N005
  { id: 'g2', left: 283,  top: 170, rotate:  5.2, src: '/photos/grid/N048.webp' }, // N048
  { id: 'g3', left: 580,  top: 85,  rotate: -3.8, src: '/photos/grid/N087.webp' },  // N087 DH-6 (was N046 Charlotte Cardin)
  { id: 'g4', left: 832,  top: 160, rotate:  6.7, src: '/photos/grid/N031.webp' },  // N031
  { id: 'g5', left: 1060, top: 100, rotate: -5.5, src: '/photos/grid/N052.webp' },  // N052 Drew Hallé
  { id: 'g6', left: 1218, top: 175, rotate:  3.4, src: '/photos/grid/N050.webp' },  // N050 Role Model
]

const CARDS_ROW2 = [
  { id: 'g7',  left: 120,  top: 448, rotate:  4.8, src: '/photos/grid/N040.webp' },  // N040
  { id: 'g8',  left: 378,  top: 498, rotate: -6.3, src: '/photos/grid/N004.webp' },  // N004 Chappell Roan
  { id: 'g9',  left: 638,  top: 442, rotate:  2.9, src: '/photos/grid/N056.webp' },  // N056 Labrinth
  { id: 'g10', left: 898,  top: 508, rotate: -5.1, src: '/photos/grid/N007.webp' },  // N007 Festival
  { id: 'g11', left: 1158, top: 452, rotate:  7.2, src: '/photos/grid/N073.webp' },  // N073
]

const ALL_CARDS = [...CARDS_ROW1, ...CARDS_ROW2]

const BOX_SHADOW_REST  = '0 14px 40px rgba(0,0,0,0.72), 0 2px 8px rgba(0,0,0,0.38), inset 0 0 0 1px rgba(255,255,255,0.06)'
const BOX_SHADOW_HOVER = '0 22px 64px rgba(0,0,0,0.82), 0 5px 14px rgba(0,0,0,0.48), inset 0 0 0 1px rgba(255,255,255,0.10)'

function Polaroid({ card, innerRef }) {
  const [tilt, setTilt]   = useState({ x: 0, y: 0 })
  const [hover, setHover] = useState(false)

  const onMove = (e) => {
    const r  = e.currentTarget.getBoundingClientRect()
    const dx = (e.clientX - r.left) / r.width  - 0.5
    const dy = (e.clientY - r.top)  / r.height - 0.5
    setTilt({ x: dy * -18, y: dx * 18 })
  }

  const onLeave = () => { setHover(false); setTilt({ x: 0, y: 0 }) }

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    transitionTo('/photography', { type: 'polaroid', rect, rotate: card.rotate, src: card.src })
  }

  const baseTransform = hover
    ? `rotate(${card.rotate * 0.35}deg) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateZ(24px) scale(1.04)`
    : `rotate(${card.rotate}deg)`

  return (
    <div
      ref={innerRef}
      data-cursor="view"
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{
        position: 'absolute',
        left: card.left,
        top: card.top,
        width: 200,
        background: 'linear-gradient(150deg, #eeeae0 0%, #e6e2d6 100%)',
        padding: '8px 8px 36px 8px',
        boxShadow: hover ? BOX_SHADOW_HOVER : BOX_SHADOW_REST,
        transform: baseTransform,
        transformStyle: 'preserve-3d',
        transition: hover
          ? 'box-shadow 300ms ease'
          : 'transform 600ms cubic-bezier(0.16,1,0.3,1), box-shadow 600ms ease',
        willChange: 'transform',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: '100%',
          height: 220,
          backgroundImage: `url(${card.src.replace('?format=1000w', '?format=500w')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    </div>
  )
}

export default function SceneGallery({ width = 1440, height = 900, isMobile = false }) {
  const containerRef  = useRef(null)
  const polaroidRefs  = useRef([])
  const hasRevealed   = useRef(false)

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasRevealed.current) {
            hasRevealed.current = true
            observer.disconnect()

            if (!prefersReduced) {
              gsap.timeline({ defaults: { ease: 'back.out(1.2)', duration: 0.85 } })
                .from(polaroidRefs.current, {
                  opacity: 0,
                  y: 80,
                  rotation: (i) => (ALL_CARDS[i]?.rotate ?? 0) * 2.5,
                  stagger: 0.08,
                })
            }
          }
        })
      },
      { threshold: 0.2 }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // ── Mobile layout: 2-column polaroid grid ────────────────────────────────
  if (isMobile) {
    // Show top 6 priority photos (first 3 of each row = 6 cards)
    const mobileCards = [...CARDS_ROW1.slice(0, 3), ...CARDS_ROW2.slice(0, 3)];
    return (
      <div style={{ width: '100%', height: '100%', background: '#030303', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header — label only */}
        <div style={{
          padding: '14px 20px 10px',
          display: 'flex', alignItems: 'center',
          flexShrink: 0,
          borderBottom: '1px solid rgba(242,239,232,0.06)',
        }}>
          <div className="mono" style={{ color: 'var(--bone-50)', fontSize: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ color: 'var(--red)' }}>05</span>
            <span style={{ width: 22, height: 1, background: 'var(--bone-30)', display: 'inline-block' }} />
            GALLERY
          </div>
        </div>

        {/* 2-column × 3-row polaroid grid — no scroll, all 6 fit */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          padding: '10px 12px 8px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr 1fr',
          gap: 9,
        }}>
          {mobileCards.map((card, i) => (
            <div
              key={card.id}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                transitionTo('/photography', { type: 'polaroid', rect, rotate: card.rotate * 0.5, src: card.src });
              }}
              style={{
                background: 'linear-gradient(150deg, #eeeae0 0%, #e6e2d6 100%)',
                padding: '4px 4px 15px 4px',
                boxShadow: '0 8px 22px rgba(0,0,0,0.65), 0 2px 5px rgba(0,0,0,0.35)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <div style={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
                overflow: 'hidden',
              }}>
                <img
                  src={card.src.replace('?format=1000w', '?format=500w')}
                  alt=""
                  loading="lazy"
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{ flexShrink: 0, padding: '10px 20px 14px', display: 'flex', justifyContent: 'center' }}>
          <TransitionLink
            to="/photography"
            style={{
              fontFamily: 'var(--font-mono, monospace)', fontSize: 9,
              color: '#ff2a1f', textDecoration: 'none',
              border: '1px solid rgba(255,42,31,0.55)',
              padding: '9px 24px', borderRadius: 2,
              background: 'rgba(255,42,31,0.08)',
              letterSpacing: '0.14em', textTransform: 'uppercase',
            }}
          >
            ALL PHOTOS →
          </TransitionLink>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width,
        height,
        background: '#030303',
        overflow: 'hidden',
        perspective: '1000px',
      }}
    >
      {/* Vignette overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          background: 'radial-gradient(ellipse at center, transparent 38%, rgba(0,0,0,0.7) 100%)',
        }}
      />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 64,
          zIndex: 30,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div
          className="mono"
          style={{ color: 'var(--bone-50)', display: 'flex', gap: 16, alignItems: 'center' }}
        >
          <span style={{ color: 'var(--red)' }}>05</span>
          <span
            style={{
              width: 40,
              height: 1,
              background: 'var(--bone-30)',
              display: 'inline-block',
            }}
          />
          GALLERY
        </div>
      </div>

      {/* Section CTA — top right */}
      <TransitionLink
        to="/photography"
        style={{
          position: 'absolute',
          top: 40,
          right: 64,
          zIndex: 30,
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: 11,
          color: '#ff2a1f',
          textDecoration: 'none',
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          border: '1px solid rgba(255,42,31,0.7)',
          padding: '10px 24px',
          borderRadius: 2,
          background: 'rgba(255,42,31,0.12)',
          backdropFilter: 'blur(4px)',
          transition: 'background 200ms ease, border-color 200ms ease, color 200ms ease, box-shadow 200ms ease',
          whiteSpace: 'nowrap',
          boxShadow: '0 0 18px rgba(255,42,31,0.18)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,42,31,0.28)';
          e.currentTarget.style.borderColor = 'rgba(255,42,31,1)';
          e.currentTarget.style.color = '#fff';
          e.currentTarget.style.boxShadow = '0 0 28px rgba(255,42,31,0.42)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,42,31,0.12)';
          e.currentTarget.style.borderColor = 'rgba(255,42,31,0.7)';
          e.currentTarget.style.color = '#ff2a1f';
          e.currentTarget.style.boxShadow = '0 0 18px rgba(255,42,31,0.18)';
        }}
      >
        VIEW ALL PHOTOS →
      </TransitionLink>

      {/* Polaroid cards — both rows */}
      {ALL_CARDS.map((card, i) => (
        <Polaroid
          key={card.id}
          card={card}
          innerRef={(el) => { polaroidRefs.current[i] = el }}
        />
      ))}

    </div>
  )
}
